import { useState } from 'react'
import { Share2, Copy, Trash2, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useShareTokens, useCreateShareToken, useDeleteShareToken } from '../hooks/useCrawls'

interface ShareReportDialogProps {
  crawlId: string
  domain: string
}

export function ShareReportDialog({ crawlId, domain }: ShareReportDialogProps) {
  const [expiryDays, setExpiryDays] = useState(30)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const { data: tokensData, isLoading } = useShareTokens(crawlId)
  const createToken = useCreateShareToken(crawlId)
  const deleteToken = useDeleteShareToken(crawlId)

  const handleCreate = () => {
    createToken.mutate(expiryDays)
  }

  const handleCopy = async (token: string) => {
    const url = `${window.location.origin}/reports/${token}`
    await navigator.clipboard.writeText(url)
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  const handleDelete = (tokenId: string) => {
    deleteToken.mutate(tokenId)
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Share2 className="h-4 w-4 mr-1.5" />
          Share Report
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Report</DialogTitle>
          <DialogDescription>
            Create a public read-only link for {domain}. Anyone with the link can view the report without logging in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Create new link */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="expiry">Expires in (days)</Label>
              <Input
                id="expiry"
                type="number"
                min={1}
                max={365}
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
              />
            </div>
            <Button onClick={handleCreate} disabled={createToken.isPending}>
              {createToken.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : null}
              Create Link
            </Button>
          </div>

          {/* Existing tokens */}
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : tokensData?.tokens && tokensData.tokens.length > 0 ? (
            <div className="space-y-2">
              <Label>Active Links</Label>
              {tokensData.tokens.map((t) => {
                const isExpired = t.expires_at && new Date(t.expires_at) < new Date()
                return (
                  <div
                    key={t.id}
                    className={`flex items-center gap-2 p-2 rounded-md border text-sm ${isExpired ? 'opacity-50' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs truncate" title={`/reports/${t.token}`}>
                        /reports/{t.token}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {isExpired
                          ? 'Expired'
                          : t.expires_at
                            ? `Expires ${new Date(t.expires_at).toLocaleDateString()}`
                            : 'No expiry'}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => handleCopy(t.token)}
                      disabled={!!isExpired}
                    >
                      {copiedToken === t.token ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(t.id)}
                      disabled={deleteToken.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              No active share links. Create one to share this report.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
